import { Link } from "@tanstack/react-router"
import { Plane } from "lucide-react"
import { useMemo } from "react"

function seededRandom(seed: number) {
  let s = seed
  return () => {
    s = (s * 16807) % 2147483647
    return (s - 1) / 2147483646
  }
}

const SHOOTING_STARS = [
  {
    top: "5%",
    left: "20%",
    rot: "75deg",
    len: "70px",
    dist: "220px",
    dur: "6s",
    delay: "0.5s",
  },
  {
    top: "3%",
    left: "55%",
    rot: "80deg",
    len: "90px",
    dist: "260px",
    dur: "8s",
    delay: "3s",
  },
  {
    top: "8%",
    left: "70%",
    rot: "72deg",
    len: "60px",
    dist: "180px",
    dur: "7s",
    delay: "5.5s",
  },
  {
    top: "2%",
    left: "35%",
    rot: "82deg",
    len: "80px",
    dist: "240px",
    dur: "9s",
    delay: "1.5s",
  },
  {
    top: "4%",
    left: "85%",
    rot: "78deg",
    len: "75px",
    dist: "200px",
    dur: "11s",
    delay: "7s",
  },
  {
    top: "6%",
    left: "10%",
    rot: "70deg",
    len: "85px",
    dist: "250px",
    dur: "10s",
    delay: "9.5s",
  },
] as const

const FLIGHT_PATH = "M 0 160 Q 120 160 220 110 Q 320 60 380 55 Q 440 50 500 25 L 530 12"

export const NotFoundPage = () => {
  const stars = useMemo(() => {
    const rand = seededRandom(42)
    return Array.from({ length: 70 }, (_, i) => ({
      key: i,
      size: rand() * 2 + 0.5,
      top: rand() * 100,
      left: rand() * 100,
      dur: rand() * 5 + 3,
      delay: rand() * 6,
      peak: rand() * 0.4 + 0.2,
    }))
  }, [])

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[linear-gradient(170deg,#0c0e1a_0%,#0f0d1f_45%,#110b1a_100%)] px-6">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {stars.map((s) => (
          <div
            key={s.key}
            className="nf-star absolute rounded-full bg-white/60"
            style={
              {
                width: s.size,
                height: s.size,
                top: `${s.top}%`,
                left: `${s.left}%`,
                "--nf-dur": `${s.dur}s`,
                "--nf-delay": `${s.delay}s`,
                "--nf-peak": s.peak,
              } as React.CSSProperties
            }
          />
        ))}
        {SHOOTING_STARS.map((s, i) => (
          <div
            key={i}
            className="nf-shooting-star"
            style={
              {
                top: s.top,
                left: s.left,
                "--nf-rot": s.rot,
                "--nf-len": s.len,
                "--nf-dist": s.dist,
                "--nf-dur": s.dur,
                "--nf-delay": s.delay,
              } as React.CSSProperties
            }
          />
        ))}
      </div>

      <div
        className="pointer-events-none absolute right-0 left-0"
        style={{ bottom: "28%" }}
        aria-hidden="true"
      >
        <div className="mx-auto h-px w-full max-w-3xl bg-[linear-gradient(90deg,transparent,rgba(99,102,241,0.3)_30%,rgba(99,102,241,0.3)_70%,transparent)]" />
      </div>

      <div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2"
        style={{ bottom: "22%" }}
        aria-hidden="true"
      >
        <svg
          width="560"
          height="180"
          viewBox="0 0 560 180"
          fill="none"
          className="overflow-visible"
        >
          <defs>
            <linearGradient id="nf-route-grad" x1="0" y1="0" x2="560" y2="0">
              <stop offset="0%" stopColor="rgba(99, 102, 241, 0.1)" />
              <stop offset="50%" stopColor="rgba(139, 92, 246, 0.45)" />
              <stop offset="100%" stopColor="rgba(167, 139, 250, 0.75)" />
            </linearGradient>
          </defs>
          <path
            className="nf-route-line"
            d={FLIGHT_PATH}
            stroke="url(#nf-route-grad)"
            strokeWidth="1.5"
            fill="none"
          />
          <circle
            className="nf-origin-dot"
            cx="0"
            cy="160"
            r="3"
            fill="rgba(99, 102, 241, 0.5)"
          />
        </svg>
        <div
          className="nf-plane absolute top-0 left-0"
          style={{ filter: "drop-shadow(0 0 8px rgba(167, 139, 250, 0.5))" }}
        >
          <Plane
            size={20}
            strokeWidth={1.5}
            className="rotate-45 text-[rgba(167,139,250,0.9)]"
          />
        </div>
      </div>

      <div className="relative z-10 text-center">
        <p className="mb-4 text-lg font-medium tracking-[0.2em] text-[rgba(139,92,246,0.75)]">
          Off course
        </p>

        <div className="text-[clamp(6rem,15vw,9rem)] leading-none font-light tracking-tight text-white/5 select-none">
          404
        </div>

        <h1 className="-mt-5 mb-3 text-2xl font-light tracking-wide text-white/88">
          Flight plan not found
        </h1>

        <p className="mx-auto mb-10 max-w-sm text-sm leading-relaxed text-white/35">
          The requested route doesn't exist in our system.
          <br />
          Let's get you back on track.
        </p>

        <Link
          to="/"
          className="inline-flex items-center gap-2.5 rounded-full border border-indigo-500/25 bg-indigo-500/12 px-7 py-3 text-sm font-medium text-[rgba(167,139,250,0.9)] transition-all duration-200 hover:border-indigo-500/50 hover:bg-indigo-500/22"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M6.5 12L2.5 8M2.5 8L6.5 4M2.5 8H13.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Return to dashboard
        </Link>
      </div>

      <style>{`
        .nf-star {
          animation: nf-twinkle var(--nf-dur) ease-in-out infinite var(--nf-delay);
        }
        @keyframes nf-twinkle {
          0%, 100% { opacity: 0.08; }
          50% { opacity: var(--nf-peak, 0.5); }
        }

        .nf-shooting-star {
          position: absolute;
          width: var(--nf-len, 80px);
          height: 1px;
          border-radius: 1px;
          transform: rotate(var(--nf-rot, 75deg));
          transform-origin: right center;
          background: linear-gradient(to right, transparent 0%, rgba(167, 139, 250, 0.01) 30%, rgba(167, 139, 250, 0.7) 100%);
          opacity: 0;
          animation: nf-shoot var(--nf-dur) var(--nf-delay) ease-in-out infinite;
          filter: drop-shadow(0 0 2px rgba(167, 139, 250, 0.4));
        }
        .nf-shooting-star::after {
          content: '';
          position: absolute;
          right: -1px;
          top: -1px;
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: rgba(220, 210, 255, 0.9);
          box-shadow: 0 0 4px 1px rgba(167, 139, 250, 0.6);
        }
        @keyframes nf-shoot {
          0%    { opacity: 0; transform: rotate(var(--nf-rot, 75deg)) translateX(0); }
          1%    { opacity: 1; }
          4%    { opacity: 0.9; }
          8%    { opacity: 0; transform: rotate(var(--nf-rot, 75deg)) translateX(var(--nf-dist, 200px)); }
          100%  { opacity: 0; }
        }

        .nf-route-line {
          stroke-dasharray: 8 6;
          animation: nf-dash 25s linear infinite;
        }
        @keyframes nf-dash {
          to { stroke-dashoffset: -280; }
        }

        .nf-origin-dot {
          animation: nf-pulse 3s ease-in-out infinite;
        }
        @keyframes nf-pulse {
          0%, 100% { r: 3; opacity: 0.4; }
          50%      { r: 5; opacity: 0.7; }
        }

        .nf-plane {
          offset-path: path("M 0 160 Q 120 160 220 110 Q 320 60 380 55 Q 440 50 500 25 L 530 12");
          offset-rotate: auto;
          animation: nf-fly 8s ease-in-out infinite;
        }
        @keyframes nf-fly {
          0%   { offset-distance: 0%;   opacity: 0; }
          5%   { opacity: 1; }
          90%  { opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .nf-star, .nf-shooting-star, .nf-route-line, .nf-plane, .nf-origin-dot { animation: none !important; }
          .nf-shooting-star { display: none; }
          .nf-plane { offset-distance: 60%; opacity: 1; }
        }
      `}</style>
    </div>
  )
}
